import React from 'react';
import type { LucideIcon } from 'lucide-react';

export type PillTabItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: LucideIcon;
  count?: number;
};

export default function PillTabs<T extends string>({
  isLight,
  ariaLabel,
  value,
  items,
  onChange,
  size = 'md',
  className = '',
}: {
  isLight: boolean;
  ariaLabel: string;
  value: T;
  items: PillTabItem<T>[];
  onChange: (id: T) => void;
  size?: 'md' | 'sm';
  className?: string;
}) {
  const compact = size === 'sm';
  const track = isLight ? 'bg-neutral-100' : 'bg-neutral-800/80';

  const itemClass = (active: boolean) => {
    const tone = active
      ? isLight
        ? 'bg-indigo-100 text-indigo-600'
        : 'bg-indigo-500/20 text-indigo-300'
      : isLight
        ? 'text-neutral-500 hover:text-neutral-800'
        : 'text-neutral-400 hover:text-neutral-100';
    const sizing = compact
      ? 'h-7 px-2.5 text-xs gap-1.5'
      : 'px-3 py-1.5 text-sm gap-2';
    return `inline-flex items-center rounded-full font-medium transition-colors ${sizing} ${tone}`;
  };

  const badgeClass = (active: boolean) => {
    const tone = active
      ? isLight
        ? 'bg-indigo-600 text-white'
        : 'bg-indigo-400 text-neutral-950'
      : isLight
        ? 'bg-neutral-200 text-neutral-500'
        : 'bg-neutral-700 text-neutral-400';
    const sizing = compact ? 'h-4 min-w-4 px-1 text-[10px]' : 'h-5 min-w-5 px-1.5 text-[11px]';
    return `inline-flex items-center justify-center rounded-full font-semibold tabular-nums leading-none ${sizing} ${tone}`;
  };

  return (
    <nav className={`inline-flex rounded-full p-1 ${track} ${className}`.trim()} aria-label={ariaLabel} role="tablist">
      {items.map((item) => {
        const Icon = item.icon;
        const active = value === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={itemClass(active)}
          >
            {Icon ? <Icon size={compact ? 12 : 15} aria-hidden /> : null}
            {item.label}
            {item.count !== undefined ? <span className={badgeClass(active)}>{item.count}</span> : null}
          </button>
        );
      })}
    </nav>
  );
}
